/**
 * 메뉴 레지스트리(/menu-config) 공유 스토어 — 관리 콘솔 "메뉴 관리"가 SSOT.
 *
 * 2026-08-11 이전에는 이 로직이 `NavBar.tsx` 안에만 있었다. 상태바(⑥)도 같은 레지스트리를
 * 기준으로 항목을 켜고 꺼야 해서(사용자 요구: "관리 콘솔 메뉴 관리 기준으로 상태바 메뉴 관리"),
 * 소비자가 둘 이상이 되었다 → 모듈 레벨 싱글턴으로 끌어올린다.
 *
 * 왜 싱글턴인가: 소비자마다 `useEffect` 로 fetch 하면 화면당 요청이 N배로 늘고(=per-IP
 * rate limit 압박, [[ratelimit-429-served-as-http-200]]), 응답 도착 순서에 따라 NavBar 와
 * StatusBar 가 서로 다른 목록을 그리는 순간이 생긴다. 요청은 **앱당 1회**, 결과는 구독자
 * 전원에게 같은 스냅샷으로 나간다.
 *
 * 폴백 순서(기존 NavBar 의미 그대로 보존):
 *   1) localStorage 캐시 — 첫 페인트 플래시 봉합
 *   2) /menu-config 응답 — 도착하면 캐시 갱신 + 전 구독자 리렌더
 *   3) 조회 실패/빈 목록 → `rows=null` (소비자는 각자의 하드코딩 fallback 사용)
 *
 * ⚠ 응답에는 **볼 수 있는 행만** 들어온다. 즉 "행이 있다 = 노출 허용"이다.
 *
 * ★★ 2026-08-25 — 응답이 **사람마다 달라졌다**(회사 층 + 부서·팀·역할·개인 층).
 *    그래서 캐시 키에 **사용자 id 를 붙인다**. 안 그러면 같은 브라우저에서 사람이 바뀌었을 때
 *    이전 사람의 메뉴가 첫 페인트에 그대로 비친다(서버 응답이 오기 전까지).
 *    키를 v2 로 올리고 옛 공용 키는 지운다.
 */

import { useSyncExternalStore } from 'react';
import { useAuthStore } from '../store/authStore';

export interface MenuConfigRow {
  menu_key: string;
  url: string;
  label: string;
  icon: string;
  /** icon 이 'custom:*' 일 때 admin_icon_registry 의 SVG 본문. */
  svg?: string;
  section: string;
  is_external: boolean;
}

/**
 * 앱 전용 위치(2026-09-15 · REQUEST-6 R6-12 사용자 결정) — 관리 콘솔 "메뉴 관리 > 위치 = 앱 전용".
 * 모바일 앱(AOS · iOS)만 쓰는 메뉴(음성 녹음 · 전자명함 · 통합 검색 · 알림)의 행이다. 웹에는 그 화면이 없으므로
 * **웹의 모든 소비자(레일 · 전체메뉴 · 즐겨찾기 · 상태바)가 받기 전에 여기서 한 번 뺀다.**
 * 이 거름이 없으면 모르는 위치는 레일 상단으로 떨어진다(NavBar rowsToCfg) — 그래서 행을 넣기 전에 이 판을 먼저 배포한다.
 */
export const MOBILE_SECTION = 'mobile';

export function isWebMenuRow(row: Pick<MenuConfigRow, 'section'>): boolean {
  return row.section !== MOBILE_SECTION;
}

const MENU_CACHE_PREFIX = 'wc.menu-config.cache.v2';
/** 사람이 바뀌면 캐시도 바뀐다 — 로그인 전이면 공용 키(그때는 어차피 사이드바가 없다). */
function menuCacheKey(): string {
  try {
    const id = useAuthStore.getState().user?.id;
    return id ? `${MENU_CACHE_PREFIX}.${id}` : MENU_CACHE_PREFIX;
  } catch {
    return MENU_CACHE_PREFIX;
  }
}

export interface MenuConfigState {
  /** 레지스트리 행(활성 메뉴만). null = 레지스트리 없음/조회 실패 → 소비자 fallback. */
  rows: MenuConfigRow[] | null;
  /** 캐시도 응답도 아직 없는 최초 로드 구간(스켈레톤). */
  pending: boolean;
}

function readCache(): MenuConfigRow[] | null {
  try {
    // 사람 단위 응답이 되기 전의 공용 캐시 — 남아 있으면 남의 메뉴가 비칠 수 있으니 지운다.
    localStorage.removeItem('wc.menu-config.cache.v1');
    const raw = localStorage.getItem(menuCacheKey());
    if (!raw) return null;
    const rows = JSON.parse(raw) as MenuConfigRow[];
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows;
  } catch {
    return null;
  }
}

let state: MenuConfigState = (() => {
  const cached = readCache();
  return { rows: cached, pending: cached === null };
})();

const subscribers = new Set<() => void>();
let started = false;

function setState(next: MenuConfigState): void {
  state = next;
  for (const fn of subscribers) fn();
}

/**
 * /menu-config 1회 조회 — 이미 시작했으면 no-op(앱당 1요청).
 *
 * ⚠ try/catch 로 감싼 이유: 이 함수는 `useSyncExternalStore` 의 subscribe 안에서 불린다.
 *   테스트(jsdom)처럼 상대경로 fetch 가 **동기 throw** 하는 환경이 있는데, 그게 밖으로
 *   새면 구독 자체가 터져 화면이 렌더되지 않는다. 조회 실패는 언제나 fallback 으로 흡수한다.
 */
/*
  ⭐⭐ 2026-10-01 — **로컬에서 레일 이름이 DB 와 다르면, 그것은 «500 폴백»이지 DB 가 안 바뀐 것이 아니다.**

  실제로 겪은 것: 로컬 DB `admin_menu_registry` 는 `label='주소록'` 인데 화면 레일은 「명함첩」을
  그리고 있었다. 「DB 가 이미 바뀌었나」로 반나절을 헤맬 뻔했다. 잰 것:
  ```
  로컬 vite  GET /menu-config  ⇒ 500   ← 이 주소는 «엣지»가 준다. vite 개발 서버에는 없다
  운영 8443  GET /menu-config  ⇒ 200   ⇒ {"menu_key":"contacts","label":"명함첩", …}
  ```
  ⇒ 그래서 로컬에서는 아래 fetch 가 늘 실패하고, 소비자가 **코드 폴백**(`NavBar` 의 `PRIMARY_NAV`
    + `layout.nav.*` i18n)을 그린다. 즉 **개발 서버에서 보는 레일 이름은 DB 가 아니라 코드다.**
  ⚠ 뒤집어 말하면 — 운영에서 이 API 가 잠깐 죽으면 사용자는 «코드에 적힌 이름»을 본다.
    그래서 폴백 낱말도 최신이어야 한다(2026-10-01 실측: `layout.nav.contacts` ko = 「명함첩」 ✅).
*/
export function ensureMenuConfigLoaded(): void {
  if (started) return;
  started = true;
  const settleAsFailure = () => setState({ rows: state.rows, pending: false });
  try {
    fetch('/menu-config', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('menu-config'))))
      .then((data: { menus?: MenuConfigRow[] }) => {
        // 2026-09-15 R6-12 — 앱 전용(section='mobile') 행은 웹의 어느 소비자에게도 넘기지 않는다(캐시에도 안 남긴다).
        const menus = (Array.isArray(data?.menus) ? data.menus : []).filter(isWebMenuRow);
        if (menus.length === 0) {
          // 레지스트리가 의도적으로 빈 응답 → 캐시 무효화 + 소비자 fallback (기존 의미 유지).
          try { localStorage.removeItem(menuCacheKey()); } catch { /* no-op */ }
          setState({ rows: null, pending: false });
          return;
        }
        try { localStorage.setItem(menuCacheKey(), JSON.stringify(menus)); } catch { /* no-op */ }
        setState({ rows: menus, pending: false });
      })
      // 조회 실패 — 캐시가 있으면 그대로 유지, 없으면 fallback.
      .catch(settleAsFailure);
  } catch {
    settleAsFailure();
  }
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  ensureMenuConfigLoaded();
  return () => { subscribers.delete(fn); };
}

function getSnapshot(): MenuConfigState {
  return state;
}

/** 레지스트리 스냅샷 구독 — NavBar/StatusBar 등 모든 소비자 공용. */
export function useMenuConfig(): MenuConfigState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * 특정 메뉴가 노출 가능한지 — 레지스트리 없으면(=조회 실패/미구성) true.
 *
 * 왜 fail-open 인가: 레지스트리를 못 읽는 상황(관리 백엔드 다운 등)에서 화면이 통째로
 * 비는 편보다 기존 메뉴가 그대로 보이는 편이 안전하다. 실제 차단은 서버(chat-server
 * menu-registry-guard)가 담당하므로 화면만 열려도 데이터는 새지 않는다.
 */
export function isMenuVisible(rows: MenuConfigRow[] | null, menuKey: string): boolean {
  if (!rows) return true;
  return rows.some((r) => r.menu_key === menuKey);
}

/** 레지스트리 행 조회 — 없으면 null. */
export function findMenu(rows: MenuConfigRow[] | null, menuKey: string): MenuConfigRow | null {
  if (!rows) return null;
  return rows.find((r) => r.menu_key === menuKey) ?? null;
}

/** 테스트 전용 — 모듈 싱글턴 초기화. */
export function _resetMenuConfigForTests(rows: MenuConfigRow[] | null = null, pending = false): void {
  started = true; // 테스트에서 실제 fetch 를 돌리지 않는다.
  setState({ rows, pending });
}
